import { HTML_ALLOWED_ATTRS, HTML_ALLOWED_TAGS, HTML_DROP_WITH_CONTENT, isSafeHtmlUrl, sanitizeStyleAttribute } from '@florezjaramillo/florezjaramillo-core'
import DOMPurify from 'dompurify'

/**
 * Limpieza de HTML para el contenido del editor.
 *
 * La política (tags, atributos y reglas de URL) vive en
 * `@florezjaramillo/florezjaramillo-core` (`utils/html-policy`) y es la misma
 * que aplica el servidor al recibir el cuerpo de la API: si cambia la lista
 * blanca, cambia en los dos lados a la vez.
 *
 * El motor es DOMPurify (el mismo que usa Markdown); la limpieza fina por
 * etiqueta —atributos permitidos y URLs seguras— se hace después sobre el DOM
 * devuelto, sin hooks globales, para no interferir con otros sanitizadores.
 */
const GLOBAL_ALLOWED_ATTRS = Array.from(new Set(Object.values(HTML_ALLOWED_ATTRS).flat()))

export function sanitizeHtml(input: string | null | undefined): string {
  if (!input) return ''

  let html = input
  // Etiquetas que se borran con su contenido: dejar el texto no tendría sentido.
  for (const tag of HTML_DROP_WITH_CONTENT) {
    html = html.replace(new RegExp(`<${tag}\\b[^>]*>[\\s\\S]*?<\\/${tag}>`, 'gi'), '')
    html = html.replace(new RegExp(`<\\/?${tag}\\b[^>]*>`, 'gi'), '')
  }

  const fragment = DOMPurify.sanitize(html, {
    RETURN_DOM: true,
    RETURN_DOM_FRAGMENT: true,
    ALLOWED_TAGS: [...HTML_ALLOWED_TAGS],
    ALLOWED_ATTR: GLOBAL_ALLOWED_ATTRS,
    ALLOW_DATA_ATTR: false
  }) as unknown as DocumentFragment

  fragment.querySelectorAll('*').forEach(el => {
    const tag = el.tagName.toLowerCase()
    const allowed = HTML_ALLOWED_ATTRS[tag] ?? []
    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase()
      if (!allowed.includes(name) || name.startsWith('on')) {
        el.removeAttribute(attr.name)
        continue
      }
      if ((name === 'href' || name === 'src') && !isSafeHtmlUrl(attr.value, tag === 'img')) {
        el.removeAttribute(attr.name)
        continue
      }
      // El `style` sostiene color, resaltado, tamaño y alineación del editor,
      // pero sólo con las propiedades de la política: lo demás se cae.
      if (name === 'style') {
        const style = sanitizeStyleAttribute(attr.value)
        if (style) el.setAttribute('style', style)
        else el.removeAttribute('style')
      }
    }
    // Un enlace que abre en otra pestaña sin `rel` deja acceso a `window.opener`.
    if (tag === 'a' && el.hasAttribute('target') && !(el.getAttribute('rel') ?? '').includes('noopener')) {
      el.setAttribute('rel', 'noopener noreferrer')
    }
  })

  const container = document.createElement('div')
  container.appendChild(fragment)
  return container.innerHTML
}
